import { FunctionComponent, useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWandMagicSparkles } from '@fortawesome/pro-regular-svg-icons';
import { type Automation } from '@spike/automation-model';
import { generateAutomationThunk, type GenerateMessageDto } from '@spike/automations-action';
import { useApiClientWrapper } from 'hooks';
import { Spinner } from 'components/UI';
import Button from 'components/UI/V3/Button/Button';
import AutomationWizardHeader from '../Wizard/Header/AutomationWizardHeader';
import AutomationCanvas from '../Canvas/AutomationCanvas';
import { mapGeneratedAutomation } from './mapGeneratedAutomation';
import styles from './AutomationAIPanel.module.scss';

const MAX_DESCRIPTION_LENGTH = 400;

const noop = () => undefined;

const extractErrorMessage = (err: unknown): string => {
    const apiMessage = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
    return apiMessage || 'Something went wrong generating this automation. Please try again.';
};

interface AutomationAIPanelProps {
    onClose: () => void;
    initialName?: string;
    onEditInBuilder: (automation: Automation) => void;
}

const AutomationAIPanel: FunctionComponent<AutomationAIPanelProps> = ({ onClose, initialName, onEditInBuilder }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const [title] = useState(initialName ?? `Automation - ${moment().format('MMM D, YYYY, h:mma')}`);
    const [messages, setMessages] = useState<GenerateMessageDto[]>([]);
    const [draftText, setDraftText] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [generatedAutomation, setGeneratedAutomation] = useState<Automation | null>(null);
    const [warnings, setWarnings] = useState<string[]>([]);
    const [error, setError] = useState<string | null>(null);

    // Guards a stale response from applying after "Stop Generating" — there's no
    // real request cancellation wired into ApiClientWrapper, so this is a soft cancel.
    const cancelledRef = useRef(false);

    const handleGenerate = useCallback(async () => {
        const trimmed = draftText.trim();
        if (!trimmed || isGenerating) return;

        const nextMessages: GenerateMessageDto[] = [...messages, { role: 'user', content: trimmed }];
        setMessages(nextMessages);
        setDraftText('');
        setError(null);
        cancelledRef.current = false;
        setIsGenerating(true);

        try {
            const result = await (dispatch as any)(generateAutomationThunk(apiClientWrapper, nextMessages));
            if (cancelledRef.current) return;

            const { automation, warnings: hydrationWarnings } = mapGeneratedAutomation(result.automation, title);
            setGeneratedAutomation(automation);
            setMessages([...nextMessages, { role: 'assistant', content: result.assistantMessage }]);
            setWarnings([...result.warnings, ...hydrationWarnings]);
        } catch (err) {
            if (!cancelledRef.current) setError(extractErrorMessage(err));
        } finally {
            if (!cancelledRef.current) setIsGenerating(false);
        }
    }, [draftText, isGenerating, messages, dispatch, apiClientWrapper, title]);

    const handleStopGenerating = () => {
        cancelledRef.current = true;
        setIsGenerating(false);
    };

    const handleEditInBuilder = () => {
        if (generatedAutomation) onEditInBuilder(generatedAutomation);
    };

    return (
        <div className={styles.container}>
            <AutomationWizardHeader
                title={title}
                onClose={onClose}
                onSave={generatedAutomation ? handleEditInBuilder : undefined}
                actionLabel="Edit in Builder"
            />
            <div className={styles.body}>
                <div className={styles.sidebar}>
                    <div className={styles.sidebarBody}>
                        <div className={styles.sidebarHeader}>
                            <FontAwesomeIcon icon={faWandMagicSparkles} />
                            <span>Create with AI</span>
                        </div>

                        {messages.length > 0 && (
                            <div className={styles.history}>
                                {messages.map((message, i) => (
                                    <div
                                        key={i}
                                        className={
                                            message.role === 'user' ? styles.historyUser : styles.historyAssistant
                                        }
                                    >
                                        {message.content}
                                    </div>
                                ))}
                            </div>
                        )}

                        <label
                            className={styles.label}
                            htmlFor="ai-automation-description"
                        >
                            Describe the automation
                        </label>
                        <div className={styles.textareaWrapper}>
                            <textarea
                                id="ai-automation-description"
                                className={styles.textarea}
                                value={draftText}
                                maxLength={MAX_DESCRIPTION_LENGTH}
                                disabled={isGenerating}
                                placeholder="When a client with the VIP tag hasn't booked an appointment in the last 3 months, send two messages with a 3 and 20 hour delay reminding them to schedule their next appointment."
                                onChange={e => setDraftText(e.target.value)}
                            />
                            <span className={styles.counter}>{MAX_DESCRIPTION_LENGTH - draftText.length}</span>
                        </div>

                        {error && <div className={styles.errorBanner}>{error}</div>}
                    </div>

                    <div className={styles.footer}>
                        <Button
                            label={messages.length > 0 ? 'Regenerate' : 'Generate'}
                            onClick={handleGenerate}
                            variant="black"
                            size="large"
                            fullWidth
                            disabled={isGenerating || !draftText.trim()}
                        />
                    </div>
                </div>

                <div className={styles.canvasArea}>
                    {isGenerating ? (
                        <div className={styles.loadingState}>
                            <Spinner />
                            <h3 className={styles.loadingTitle}>Generating Automation</h3>
                            <p className={styles.loadingSubtitle}>This could take a moment.</p>
                            <button
                                type="button"
                                className={styles.stopButton}
                                onClick={handleStopGenerating}
                            >
                                Stop Generating
                            </button>
                        </div>
                    ) : generatedAutomation ? (
                        <>
                            {warnings.length > 0 && (
                                <div className={styles.warningsBanner}>
                                    {warnings.map((warning, i) => (
                                        <div key={i}>{warning}</div>
                                    ))}
                                </div>
                            )}
                            <div className={styles.canvasWrapper}>
                                <AutomationCanvas
                                    steps={generatedAutomation.steps}
                                    onInsert={noop}
                                    onInsertBranch={noop}
                                    onRemove={noop}
                                    readOnly
                                />
                            </div>
                        </>
                    ) : (
                        <div className={styles.emptyState}>
                            <FontAwesomeIcon
                                icon={faWandMagicSparkles}
                                className={styles.emptyIcon}
                            />
                            <h3 className={styles.emptyTitle}>Describe the flow to get started</h3>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AutomationAIPanel;
